import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import { defineStore, acceptHMRUpdate } from 'pinia';
import { getDefaultTimeseries } from '@/reports/utils';
import {
    fetchSavedViews,
    fetchSavedView,
    createSavedView,
    deleteSavedView,
    updateSavedView,
} from '@/vuex/modules/saved-views/saved-views.api';
import { formatSavedFilter, unmapFilters, REPLAY_LIBRARY, SAVED_VIEWS_TYPES } from '@/stores/saved-filters.utils';
import { useUserSettingsStore } from '@/stores/user-settings.store';

interface SavedFilter {
    id: string;
    type: string;
    filters?: unknown;
    appIds?: unknown;
    segmentId?: unknown;
    locked?: boolean;
    [key: string]: unknown;
}

interface UserSetting {
    value?: {
        id?: string | number;
    };
}

interface SavedFiltersState {
    map: Record<string, Record<string | number, SavedFilter>>;
    error: unknown;
    fetching: boolean;
    deleting: boolean;
    tempSavedFilter: Partial<SavedFilter>;
}

function getInitialState (): SavedFiltersState {
    return {
        map: {
            [REPLAY_LIBRARY]: {},
        },
        error: {},
        fetching: false,
        deleting: false,
        tempSavedFilter: {},
    };
}

export const useSavedFiltersStore = defineStore('savedFilters', {
    state: getInitialState,
    getters: {
        savedFiltersList (state) {
            return (type: string) => Object.values(state.map[type]).map(
                formatSavedFilter as (item: SavedFilter) => SavedFilter,
            );
        },
        savedFilterById (state) {
            return (id: string | number) => {
                const type = Object.values(SAVED_VIEWS_TYPES).find((t) => get(state.map[t as string], id));

                if (type) return formatSavedFilter(state.map[type as string][id]);

                return null;
            };
        },
        defaultReplayLibrarySavedFilter (): SavedFilter | undefined {
            return this.savedFiltersList(REPLAY_LIBRARY).find((savedFilter) => savedFilter.locked);
        },
        defaultReplayLibrarySavedFilterId (): string | number {
            return this.defaultReplayLibrarySavedFilter?.id || 'savedFilterId';
        },
        lastSavedFilterId (): string | number {
            const lastSavedFilterSetting = useUserSettingsStore().settingByName(
                'recentReplayLibrarySavedFilter',
                true,
            ) as UserSetting | undefined;

            return get(lastSavedFilterSetting, 'value.id') || this.defaultReplayLibrarySavedFilterId;
        },
        latestSavedFilter (state): Record<string, unknown> {
            const { tempSavedFilter } = state;
            const lastSavedFilter = this.savedFilterById(this.lastSavedFilterId);

            let view: Record<string, unknown> = { filters: {} };
            if (!isEmpty(tempSavedFilter)) {
                view = { ...tempSavedFilter };
            } else if (lastSavedFilter && !isEmpty(lastSavedFilter.filters)) {
                view = { ...lastSavedFilter };
            }

            const filters = view.filters as Record<string, unknown> | undefined;
            if (!view.appIds && filters?.appId) {
                view.appIds = filters.appId;
            }

            return {
                ...view,
                segmentId: get(view, 'segmentId', 'everyone'),
                filters: {
                    ...(view.filters as object),
                    timeSeries: get(view, 'filters.timeSeries', getDefaultTimeseries()),
                },
            };
        },
    },
    actions: {
        setMap ({ map, type }: { map: Record<string | number, SavedFilter>; type: string }) {
            this.map[type] = map;
        },
        setFetching ({ fetching }: { fetching: boolean }) {
            this.fetching = fetching;
        },
        setSavedFilter ({ savedFilter }: { savedFilter: SavedFilter }) {
            const { id, type } = savedFilter;
            this.map[type][id] = savedFilter;
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        removeSavedFilter ({ savedFilter }: { savedFilter: SavedFilter }) {
            const { id, type } = savedFilter;
            delete this.map[type][id];
        },
        setDeleting ({ deleting }: { deleting: boolean }) {
            this.deleting = deleting;
        },
        setTempSavedFilter ({ tempSavedFilter }: { tempSavedFilter: Partial<SavedFilter> }) {
            this.tempSavedFilter = tempSavedFilter;
        },
        async loadAll ({ reFetch = false, type }: { reFetch?: boolean; type?: string } = {}) {
            if (!isEmpty(this.map[type as string]) && !reFetch) {
                return;
            }
            try {
                this.setFetching({ fetching: true });
                this.setError({ error: {} });
                const fetchedSavedFilters = await fetchSavedViews(type);
                this.setMap({ map: keyBy(fetchedSavedFilters as SavedFilter[], 'id'), type: type as string });
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setFetching({ fetching: false });
            }
        },
        async loadOne ({ reFetch = false, id, type }: { reFetch?: boolean; id: string | number; type: string }) {
            if (!reFetch && this.map[type][id]) {
                return;
            }
            try {
                this.setFetching({ fetching: true });
                this.setError({ error: {} });
                const savedFilter = await fetchSavedView(id as string) as SavedFilter;
                this.setSavedFilter({ savedFilter });
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setFetching({ fetching: false });
            }
        },
        async create ({ savedFilter }: { savedFilter: SavedFilter }) {
            try {
                const newSavedFilter = {
                    ...savedFilter,
                    filters: unmapFilters(savedFilter.filters as object | undefined),
                };
                const createdSavedFilter = await createSavedView(newSavedFilter) as SavedFilter;
                this.setSavedFilter({ savedFilter: createdSavedFilter });

                return createdSavedFilter;
            } catch (error) {
                this.setError({ error });
                throw error;
            }
        },
        async delete ({ savedFilter }: { savedFilter: SavedFilter }) {
            try {
                this.setDeleting({ deleting: true });
                await deleteSavedView(savedFilter.id);
                this.removeSavedFilter({ savedFilter });
            } catch (error) {
                this.setError({ error });
                throw error;
            } finally {
                this.setDeleting({ deleting: false });
            }
        },
        async update ({ savedFilter, instantUpdate = false }: { savedFilter: SavedFilter; instantUpdate?: boolean }) {
            let existingSavedFilter: SavedFilter | undefined;
            try {
                const savedFilterToUpdate = {
                    ...savedFilter,
                    filters: unmapFilters(savedFilter.filters as object | undefined),
                };

                if (instantUpdate) {
                    const { id, type } = savedFilter;
                    existingSavedFilter = { ...this.map[type][id] };
                    this.setSavedFilter({ savedFilter: savedFilterToUpdate as SavedFilter });
                }

                const updatedSavedFilter = await updateSavedView(savedFilterToUpdate) as SavedFilter;
                this.setSavedFilter({ savedFilter: updatedSavedFilter });
            } catch (error) {
                this.setError({ error });
                if (existingSavedFilter) {
                    this.setSavedFilter({ savedFilter: existingSavedFilter });
                }

                throw error;
            }
        },
        updateTempSavedFilter ({ tempSavedFilter }: { tempSavedFilter: Partial<SavedFilter> }) {
            this.setTempSavedFilter({ tempSavedFilter });
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSavedFiltersStore, module.hot));
}
